import { getCustomerClaims, getCustomerOrders, getCustomerPolicies } from '@/service/customerServices';
import { buildOrderCases, buildProductCases } from '@/utils/productCases';
import type { ProductCase } from '@/types/product';
import { useDashboardQuery } from './useDashboardQuery';

const EMPTY: ProductCase[] = [];

async function loadSources() {
  return Promise.all([
    getCustomerPolicies(),
    getCustomerOrders(),
    // Claims only enrich the cards; don't fail the page if they can't load.
    getCustomerClaims().catch(() => []),
  ]);
}

/** Products for My Plans (one card per product, including policies without an order). */
export function useCustomerProducts() {
  const { data, ...rest } = useDashboardQuery(
    async (demo) => {
      if (import.meta.env.DEV && demo) return (await import('@/pages/dev/demoData')).demoProductCases();
      return buildProductCases(...(await loadSources()));
    },
    EMPTY,
    'We couldn’t load your products. Please try again.',
  );
  return { products: data, ...rest };
}

/**
 * Orders for the Orders tab and order details (one case per order, newest first).
 * `withShipmentSamples` adds the demo session's extra shipment states (S2/S3) for pages
 * that look an order up by ID, without adding them to the Orders list.
 */
export function useCustomerOrders({ withShipmentSamples = false } = {}) {
  const { data, ...rest } = useDashboardQuery(
    async (demo) => {
      if (import.meta.env.DEV && demo) {
        const { demoOrderCases, demoShipmentSamples } = await import('@/pages/dev/demoData');
        return withShipmentSamples ? [...demoOrderCases(), ...demoShipmentSamples()] : demoOrderCases();
      }
      return buildOrderCases(...(await loadSources()));
    },
    EMPTY,
    'We couldn’t load your orders. Please try again.',
  );
  return { orders: data, ...rest };
}
